<template>
  <DrawerVue :refreshDisplay="refreshDisplay" :scale="1.5">
    <v-container v-if="
      !profileView && !readonly && slotData && slotData.id &&
      !slotData.accepted_by_therapist &&
      slotData.date >= today">
      <v-row>
        <v-col cols="auto">
          <h2>
            {{ $t("messages.timeSlotSuggestion") }}
          </h2>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="4" @click="setStatus(deniedByTherapistStatus)">
          <v-btn class="primaryButton" large block flat>
            {{ $t("basics.deny") }}
          </v-btn>
        </v-col>
        <v-col cols="8">
          <v-btn class="successButton" flat large block @click="acceptSuggestion()">
            {{ $t("basics.accept") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="bookingCancellationDialog" max-width="560" :persistent="cancellingBookingID !== null">
      <v-card>
        <v-card-title>{{ $t('messages.cancelPatientBookingTitle') }}</v-card-title>
        <v-card-text>
          <p>{{ bookingToCancel?.user?.full_name }}</p>
          <p>{{ $t('messages.cancelPatientBookingSeriesInfo') }}</p>
          <p>{{ $t('messages.cancelPatientBookingCheckoutInfo') }}</p>
          <v-alert type="warning" variant="tonal" class="mt-3">
            {{ $t('messages.cancelPatientBookingRefundWarning') }}
          </v-alert>

          <p class="mt-3">
            {{ $t('messages.unsavedEditorChangesDiscarded') }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" :disabled="cancellingBookingID !== null"
            @click="bookingCancellationDialog = false">{{ $t('basics.back') }}</v-btn>
          <v-spacer />
          <v-btn color="error" variant="text" :loading="cancellingBookingID !== null"
            :disabled="cancellingBookingID !== null" @click="cancelPatientBooking">{{ $t('basics.confirmCancellation') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-container>
      <v-row class="my-4">
        <v-col cols="auto">
          <h2 v-if="!slotData || slotData.created">
            {{ $t("basics.new", { type: $t("basics.appointment") }) }}
          </h2>
          <h2 v-else>
            {{ $t("basics.edit", { type: $t("basics.appointment") }) }}
          </h2>
        </v-col>

        <v-spacer />

        <v-col :cols="isMobile ? 12 : 'auto'" v-if="slotData && slotData.id"
          :class="{ 'pa-0': !isMobile, 'pt-0': isMobile }" style="display: flex; align-items: center;">
          <div style="display: flex">
            <v-tooltip :text="$t('basics.delete')" v-if="userCanDeleteTimeSlot && slotData.deletable">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" @click="showDeleteTimeSlot()" class="icon-transparency hoverable ml-4">
                  mdi-trash-can
                </v-icon>
              </template>
            </v-tooltip>

            <v-tooltip :text="$t('messages.addFollowUpFor')">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" @click="duplicateTimeSlot()" class="icon-transparency hoverable ml-4">
                  mdi-content-copy
                </v-icon>
              </template>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="slotData && slotData.errors && slotData.errors.length && !profileView">
        <v-col class="pt-0 mb-4">
          <v-card flat>
            <v-container fluid>
              <v-row>
                <v-col>
                  <TimeSlotErrorsVue :timeSlotErrorParam="slotData.errors" />
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
      </v-row>

      <v-form ref="slotEditForm">
        <v-row v-if="profileView">
          <v-col>
            <h1>{{ getReadableDate(date) }}</h1>
            <h2>
              {{ getReadableTime(startTime) }} - {{ getReadableTime(endTime) }} {{ $t("basics.oClock") }}</h2>
          </v-col>
        </v-row>

        <v-row>
          <v-col v-if="
            slotData && slotData.id && !profileView">
            <v-select variant="outlined" v-model="status" large block :label="$t('basics.status')"
              :item-title="(item) => $t('constants.timeslot_status_' + item.value)" :items="statusChoices"
              return-object>
            </v-select>
          </v-col>

          <v-col>
            <v-select :label="$t('basics.product') + '*'" :items="availProducts" return-object variant="outlined"
              v-model="product" class="py-0" item-title="name" :readonly="readonly" :disabled="this.profileView && this.availProducts.length == 1 && this.product != undefined
                ">
            </v-select>
          </v-col>
        </v-row>

        <v-row v-if="!profileView">
          <v-col>
            <v-menu v-model="datePicker" :close-on-content-click="false" transition="v-scale-transition"
              min-width="auto" :readonly="readonly">
              <template v-slot:activator="{ props }">
                <v-text-field v-bind="props" variant="outlined" flat :model-value="getReadableDate(date)" readonly
                  :label="$t('basics.startDate') + '*'" append-inner-icon="mdi-calendar"
                  :rules="[requiredRule]"></v-text-field></template>
              <v-date-picker v-model="date" v-if="datePicker" color="rgb(var(--v-theme-primary))" :show-week="true"
                hide-header @update:modelValue="datePicker = false"></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-row v-if="!profileView">
          <v-col>
            <v-select :label="$t('basics.startTime') + '*'" :items="times" variant="outlined" v-model="startTime"
              class="py-0" item-title="start_time" :readonly="readonly">
            </v-select>
          </v-col>
          <v-col>
            <v-select :label="$t('basics.endTime') + '*'" :items="times" variant="outlined" v-model="endTime"
              class="py-0" item-title="end_time" :readonly="readonly">
            </v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class="pt-0">
            <v-combobox :readonly="readonly" :label="$t('basics.therapist')" :items="availTeachers" v-model="teachers"
              variant="outlined" flat :multiple="true" return-object autocomplete="off"
              :item-title="(item) => item.first_name + ' ' + item.last_name"
              :item-value="(item) => item.first_name + ' ' + item.last_name" :rules="[requiredRule]">
              <template v-slot:item="{ props, item }">
                <div v-bind="props" class="pa-2 hoverable" :style="{
                  opacity: isAvailable(teachersFiltered, item.raw) ? 1 : 0.3,
                }">
                  {{ item.raw.first_name }} {{ item.raw.last_name }}</div>
              </template>
            </v-combobox>
          </v-col>
        </v-row>

        <v-row v-if="!profileView">
          <v-col cols="12" class="pt-0">
            <v-combobox :readonly="readonly" autocomplete="off" :label="$t('messages.followUpFor')" :items="availPrevs"
              v-model="prev" variant="outlined" flat :multiple="false" return-object
              :item-title="(item) => item.display_date" :item-value="(item) => item.display_date" :rules="[validObject]"
              @update:modelValue="setPrevValues" @update:search="fetchPrevs" no-filter>
              <template v-slot:item="{ props, item }">
                <div v-bind="props" class="pa-2 hoverable">
                  <b>{{ item.raw.display_date }} - {{ item.raw.product?.name }}</b><br>
                  <span v-for="(patient, patientIndex) in item.raw.patients">
                    <span v-if=patient.user>{{ patient.user.full_name }}</span>
                    <span v-else>{{ $t("messages.noUser") }}</span>

                    <span v-if="patientIndex < item.raw.patients.length - 1">, </span>
                  </span>
                  <span v-if="!item.raw.patients || item.raw.patients.length == 0">
                    <span>{{ $t("messages.noUser") }}</span>
                  </span>
                  <v-divider />
                </div>
              </template>
            </v-combobox>
          </v-col>
        </v-row>

        <v-row v-if="!profileView">
          <v-col>
            <v-radio-group inline v-model="location" @update:model-value="setTravelTimes">
              <v-radio v-for="loc in locations" :label="$t('constants.locations_' + loc.value)"
                :value="loc.key"></v-radio>
            </v-radio-group>
          </v-col>
        </v-row>

        <v-row v-if="isInternalLocation && !profileView">
          <v-col cols="12" class="pt-0">
            <v-select :readonly="readonly" autocomplete="off" :label="$t('basics.room')" :items="availRooms"
              v-model="rooms" variant="outlined" flat :multiple="true" close-on return-object
              :rules="isExternalLocation ? [validObject] : [requiredRooms, validObject]" close-
              :item-title="(item) => item.name" :item-value="(item) => item.name">
              <template v-slot:item="{ props, item }">
                <div v-bind="props" class="pa-2 hoverable" :style="{
                  opacity: isAvailable(roomsFiltered, item.raw) ? 1 : 0.3,
                }">
                  {{ item.raw.name }} <i>{{ item.raw.premise?.name }}</i></div>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <v-row v-else-if="!profileView">
          <v-col class="pt-0">
            <v-text-field :label="$t('basics.customAddress')" variant="outlined" v-model="customAddress" class="py-0"
              :rules="[max256]" :readonly="readonly">
            </v-text-field>
          </v-col>
        </v-row>

        <v-row v-if="!profileView">
          <v-col cols="12" v-if="isExternalLocation">
            {{ $t("messages.travelTimeInfoText") }}
          </v-col>
          <v-col cols="12" v-else>
            {{ $t("messages.prepTimeInfoText") }}
          </v-col>
          <v-col class="pt-0">
            <v-text-field :label="isExternalLocation ? $t('basics.travelTimePrev') : $t('basics.prepTimePrev')"
              variant="outlined" v-model="travelTimePrev" class="py-0" type="number" :readonly="readonly">
            </v-text-field>
          </v-col>
          <v-col class="pt-0">
            <v-text-field :label="isExternalLocation ? $t('basics.travelTimeAfter') : $t('basics.prepTimeAfter')"
              variant="outlined" v-model="travelTimeAfter" class="py-0" type="number" :readonly="readonly">
            </v-text-field>
          </v-col>
        </v-row>


        <v-row v-if="!profileView">
          <v-col v-for="(patient, pIndex) in patients" cols="12" class="pt-0">
            <v-card flat v-if="patient">
              <v-container fluid class="pa-2">
                <v-row>
                  <v-col cols="auto" style="width: 32px; display: flex; align-items:center">
                    <span v-if="!patient.id && !readonly" @click="removePatient(pIndex)"> <v-icon
                        style="margin-top: -2px">mdi-minus</v-icon></span>
                  </v-col>
                  <v-col style="display: flex; align-items:center">
                    {{ patient.user.full_name }}
                    <div class="mt-1">
                      <v-chip size="small"
                        :color="patient.is_cancelled ? 'error' : patient.is_paid ? 'success' : 'warning'">
                        {{ $t(patient.is_cancelled ? 'basics.cancelled' : patient.is_paid ? 'basics.paid' : 'basics.open') }}
                      </v-chip>
                      <small v-if="patient.is_cancelled" class="d-block mt-1">
                        {{ $t(patient.is_paid ? 'messages.paidWithoutAutomaticRefund' : 'messages.notPaid') }}
                        <span v-if="patient.cancelled_at"> {{ $t('messages.bookingCancelledOn', { date: getReadableDate(patient.cancelled_at) }) }}</span>
                      </small>
                      <v-btn
                        v-if="!readonly && patient.id && patient.can_cancel_booking && $checkFunctionPermission(['cancel_patient_booking'])"
                        size="small" variant="text" color="error" class="ml-2" :disabled="cancellingBookingID !== null"
                        @click="showBookingCancellation(patient)">
                        {{ $t('basics.cancelBooking') }}
                      </v-btn>
                    </div>
                  </v-col>
                  <v-col :cols="priceCols" style="display: flex; align-items:center">
                    <v-text-field class="hideDetails" v-model="patient.price"
                      :readonly="readonly || patient.is_paid || patient.checkout_reserved || patient.has_bill || patient.is_cancelled"
                      type="number" step="0.01" :label="$t('basics.net')" @blur="calcBrut(patient)" variant="solo"
                      :class="{ 'mr-4': product && product.is_psycotherapy }">
                    </v-text-field>

                    <v-text-field v-if="product && !product.is_psycotherapy" class="hideDetails ml-4"
                      v-model="patient.priceBrut"
                      :readonly="readonly || patient.is_paid || patient.checkout_reserved || patient.has_bill || patient.is_cancelled"
                      type="number" step="0.01" :label="$t('basics.brut')" @blur="calcNet(patient)" variant="solo">
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-if="!profileView">
          <v-col cols="12" class="pt-0">
            <v-combobox autocomplete="off" :readonly="readonly" :label="$t('basics.patient')" :items="availPatients"
              v-model="currPatient" @update:modelValue="addPatient" @update:search="fetchPatients" no-filter
              variant="outlined" flat :multiple="false" return-object :rules="[]" :item-title="(item) => item.full_name"
              :item-value="(item) => item.full_name">
              <template v-slot:item="{ props, item }">
                <div v-bind="props" class="pa-2 hoverable">
                  {{ item.raw.full_name }}
                  <div v-if="item.raw.readable_birthday">{{
                    item.raw.readable_birthday }}</div>
                  <div>{{ item.raw.display_address }}</div>
                  <v-divider />
                </div>
              </template>
              <template v-slot:selection="{ item }">
                <div>
                  {{ item.raw.full_name }}
                </div>
              </template>
            </v-combobox>
          </v-col>
        </v-row>

        <v-row v-if="slotData && !slotData.id && !profileView">
          <v-col>
            <v-checkbox :disabled="patients.length > 1" class="hideDetails" v-model="suggestForPatient"
              :label="$t('messages.appointmentSuggestionPatient')">
            </v-checkbox>
          </v-col>
          <v-col cols="auto">
            <v-tooltip :text="$t('messages.appointmentSuggestionPatientInfo')">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>

          </v-col>
        </v-row>

        <v-row v-if="!profileView">
          <v-col>
            <v-checkbox :disabled="!product || product.display_status != 'public'" class="hideDetails" v-model="public"
              :label="$t('messages.publicAppointment')">
            </v-checkbox>
          </v-col>
          <v-col cols="auto" style="display: flex; align-items: center;">
            <v-tooltip :text="$t('messages.publicAppointmentInfo')">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
          </v-col>
        </v-row>

        <v-row v-if="public">
          <v-col>
            <v-text-field :disabled="!product || product.display_status != 'public'" variant="outlined" flat
              v-model="numPatients" :label="$t('basics.numPatients') + '*'" type="number"
              :rules="[requiredRule]"></v-text-field>
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <v-row>
          <v-col>
            <v-textarea :label="$t('basics.comment')" variant="outlined" v-model="description" class="py-0"
              :rules="[max5000]" :readonly="readonly">
            </v-textarea>
          </v-col>
        </v-row>

        <!-- <v-row class="mb-8">
          <v-col cols="auto">
            {{ $t("basics.color") }}
          </v-col>
          <v-col cols="6">
            <color-picker v-model:pureColor="colorCode" />
          </v-col>
          <v-col @click="colorCode = undefined">
            <v-icon class="icon-transparency">mdi-close</v-icon>
          </v-col>
        </v-row> -->

        <v-row v-if="profileView">
          <v-col cols="12"><small>{{ $t('messages.bookingCancellationByPhone') }}</small></v-col>
        </v-row>
        <v-row v-if="!readonly && !profileView">
          <v-col cols="12">
            <v-btn large block flat class="primaryButton" @click="confirm()" ref="confirmButton" style="width: 100%">
              <span>{{ $t("basics.confirm") }}</span>
            </v-btn>
          </v-col>
        </v-row>
        <v-row
          v-if="!readonly && !profileView && slotData && slotData.id && slotData.accepted_by_patient && slotData.display_status == 'planned'"
          class="mb-8">
          <v-col cols="12">
            <v-btn large block flat @click="confirm(true)" ref="confirmButton"
              style="width: 100%; background-color: rgb(var(--v-theme-secondary)); color: rgb(var(--v-theme-light))">
              <v-icon class="mr-4">
                mdi-email-outline
              </v-icon>
              <span>{{ $t("messages.saveAndSendAppointmentChangedMail") }}</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
  </DrawerVue>
</template>

<script>
import FancyComboboxVue from "../main/FancyCombobox.vue";
import TimeSlotErrorsVue from "./TimeSlotErrors.vue";

export default {
  name: "slotEditComponent",

  props: {
    refreshDisplay: {},
    slotData: {},
    times: {},
    duration: {},
    readonly: {
      default: false,
    },
    locations: {
      required: true
    },
    statusChoices: {
      required: true
    }
  },

  emits: ["duplicate", "delete", "loading", "close"],

  data() {
    return {
      bookingCancellationDialog: false,
      bookingToCancel: null,
      cancellingBookingID: null,
      date: undefined,
      datePicker: false,

      dateIndex: 1,

      description: undefined,

      startTime: undefined,
      endTime: undefined,

      public: false,
      numPatients: 1,

      colorCode: undefined,

      isTemporary: false,

      teachers: [],
      availTeachers: [],
      teachersFiltered: [],

      location: undefined,

      rooms: undefined,
      availRooms: [],
      roomsFiltered: [],

      patients: [],
      currPatient: undefined,
      availPatients: undefined,

      acceptedByPatient: false,
      acceptedByTherpist: true,

      suggestForPatient: false,

      status: undefined,

      availPrevs: [],
      prev: undefined,

      availProducts: [],
      product: undefined,

      customAddress: "",

      travelTimePrev: 0,
      travelTimeAfter: 1,

      today: this.$moment(new Date()).format("YYYY-MM-DD"),

      requiredRule: (value) => !!value || this.$i18n.t("messages.required"),
      max256: (value) =>
        !value || value.length <= 256 || this.$i18n.t("messages.tooLong", { count: 256 }),
      max5000: (value) =>
        !value ||
        value.length <= 5000 ||
        this.$i18n.t("messages.tooLong", { count: 5000 }),
      validObject: (value) =>
        !value ||
        typeof value == "object" ||
        this.$i18n.t("messages.validSelectionrequired"),

      requiredRooms: (value) => !!value && value.length > 0 || this.$i18n.t("messages.required"),
    };
  },

  components: {
    FancyComboboxVue,
    TimeSlotErrorsVue
  },

  computed: {
    deniedByTherapistStatus() {
      if (!this.statusChoices) {
        return
      }

      for (let i = 0; i < this.statusChoices.length; i++) {
        if (this.statusChoices[i].value == "denied_by_therapist") {
          return i
        }
      }

      return
    },
    isInternalLocation() {
      if (!this.locations) {
        return false
      }

      for (let i = 0; i < this.locations.length; i++) {
        if (this.locations[i].value == "internal") {
          return this.location == this.locations[i].key
        }
      }

      return false
    },
    isExternalLocation() {
      if (!this.locations) {
        return false
      }

      for (let i = 0; i < this.locations.length; i++) {
        if (this.locations[i].value == "external") {
          return this.location == this.locations[i].key
        }
      }

      return false
    },
    userCanDeleteTimeSlot() {
      return this.$checkFunctionPermission(["time_slot_delete_detail"])
    },
    profileView() {
      return this.$route.path.startsWith("/main")
    },
    textCols() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return 12;
        case "sm":
          return 12;
        case "md":
          return 4;
        case "lg":
          return 4;
        case "xl":
          return 3;
        case "xxl":
          return 3;
        default:
          return 3;
      }
    },
    isMobile() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return true;
        case "sm":
          return true;
        case "md":
          return true;
        case "lg":
          return false;
        case "xl":
          return false;
        case "xxl":
          return false;
        default:
          return false;
      }
    },
    priceCols() {
      if (this.product && this.product.is_psycotherapy) {
        switch (this.$vuetify.display.name) {
          case "xs":
            return undefined;
          case "sm":
            return undefined;
          case "md":
            return undefined;
          case "lg":
            return 3;
          case "xl":
            return 3;
          case "xxl":
            return 3;
          default:
            return 3;
        }
      }

      switch (this.$vuetify.display.name) {
        case "xs":
          return 12;
        case "sm":
          return 12;
        case "md":
          return 12;
        case "lg":
          return 6;
        case "xl":
          return 6;
        case "xxl":
          return 6;
        default:
          return 6;
      }
    },
  },

  watch: {
    refreshDisplay() {
      if (this.refreshDisplay) {
        this.setValues();

        this.fetchTimeSlotData().then(() => {
          this.fetchProducts().then(() => {
            if (!this.profileView) {
              this.fetchPrevs().then(() => this.fetchPatients())
            }
          })
        });
      }
    },
  },

  methods: {
    showBookingCancellation(patient) {
      if (this.profileView || this.readonly || !patient.can_cancel_booking || this.cancellingBookingID !== null) return;
      this.bookingToCancel = patient;
      this.bookingCancellationDialog = true;
    },
    async cancelPatientBooking() {
      const patient = this.bookingToCancel;
      if (!patient || this.profileView || this.readonly || this.cancellingBookingID !== null) return;
      this.cancellingBookingID = patient.id;
      try {
        const response = await this.axios.post(
          this.axios.defaults.baseURL + 'calendar/timeslots/patients/cancel/',
          { booking_id: patient.id }
        );
        if (response.data.cancelled !== true) throw new Error(this.$t('messages.bookingCancellationNotConfirmed'));
        this.bookingCancellationDialog = false;
        this.$emit('close', true);
      } catch (error) {
        window.dispatchEvent(new CustomEvent('showErrorDisplay', { detail: error }));
      } finally {
        this.cancellingBookingID = null;
      }
    },
    calcNet(patient) {
      if (this.readonly || patient.is_paid || patient.checkout_reserved || patient.has_bill || patient.is_cancelled) return;
      console.log("net")
      if (!patient.priceBrut) {
        patient.price = 0
      }
      else {
        patient.price = +(patient.priceBrut / 1.19).toFixed(2);
      }
    },
    calcBrut(patient) {
      if (this.readonly || patient.is_paid || patient.checkout_reserved || patient.has_bill || patient.is_cancelled) return;
      console.log("brut")
      if (!patient.price) {
        patient.priceBrut = 0
      }
      else {
        patient.priceBrut = +(patient.price * 1.19).toFixed(2);
      }
    },
    duplicateTimeSlot() {
      this.$emit("duplicate")
    },
    setPrevValues(newVal) {
      console.log(newVal)

      this.location = newVal.location
      this.numPatients = newVal.num_patients

      this.patients = JSON.parse(JSON.stringify(newVal.patients || [])).filter(patient => !patient.is_cancelled)

      for (let i = 0; i < this.patients.length; i++) {
        this.patients[i].id = undefined;
        this.patients[i].is_paid = false;
        this.patients[i].is_cancelled = false;
        this.patients[i].checkout_reserved = false;
        this.patients[i].has_bill = false;
        this.patients[i].booking_status = 'open';
        this.patients[i].can_cancel_booking = false;
        this.patients[i].paid_at = null;
        this.patients[i].cancelled_at = null;
      }

      this.public = newVal.public
      this.travelTimePrev = newVal.travel_time_prev
      this.travelTimeAfter = newVal.travel_time_after

      this.rooms = []
      for (let i = 0; i < newVal.rooms.length; i++) {
        for (let j = 0; j < this.availRooms.length; j++) {
          if (newVal.rooms[i].id == this.availRooms[j].id) {
            this.rooms.push(newVal.rooms[i])
          }
        }
      }
    },
    setTravelTimes() {
      if (this.isExternalLocation) {
        if (!this.travelTimePrev) {
          this.travelTimePrev = 1
        }
      }

      if (!this.travelTimeAfter) {
        this.travelTimeAfter = 1
      }
    },
    setStatus(key) {
      if (!this.slotData.id) {
        return
      }

      let formData = {
        id: this.slotData.id,
        status: key
      }

      this.patchOrPostData(formData)
    },
    acceptSuggestion() {
      if (!this.slotData.id) {
        return
      }

      let formData = {
        id: this.slotData.id,
        accepted_by_therapist: true
      }

      this.patchOrPostData(formData)
    },
    addPatient(val) {
      if (typeof this.currPatient == "string") {
        return
      }

      if (this.currPatient == undefined) {
        return
      }

      let found = false
      for (let i = 0; i < this.patients.length; i++) {
        if (!this.patients[i].is_cancelled && this.patients[i].user.id == this.currPatient.id) {
          found = true
          break
        }
      }

      if (!found) {
        this.patients.push({
          user: this.currPatient,
          price: this.product?.price,
          priceBrut: +(this.product?.price * 1.19).toFixed(2)
        })
      }

      this.currPatient = undefined

      if (this.patients.length > 1) {
        this.suggestForPatient = false
      }
    },
    removePatient(patientIndex) {
      this.patients.splice(patientIndex, 1)
    },
    showDeleteTimeSlot() {
      this.$emit("delete")
    },
    isAvailable(list, item) {
      for (let i = 0; i < list.length; i++) {
        if (list[i].id == item.id) {
          return true;
        }
      }

      return false;
    },
    setValues() {
      if (this.slotData) {
        this.date = this.slotData.date;
        this.description = this.slotData.description;

        this.prev = this.slotData.prev

        if (this.slotData.color_code) {
          this.colorCode = this.hexToRgbString(this.slotData.color_code);
        } else {
          this.colorCode = undefined;
        }

        if (this.slotData.time_units.length > 0) {
          this.startTime = this.slotData.time_units[(this.slotData.travel_time_prev || 0)].start_time;
          this.endTime = this.slotData.time_units[
            this.slotData.time_units.length - 1 - (this.slotData.travel_time_after || 0)
          ].end_time;
        }

        if (this.slotData.teachers) {
          this.teachers = JSON.parse(JSON.stringify(this.slotData.teachers));
        } else {
          this.teachers = [];
        }

        if (this.slotData.rooms) {
          this.rooms = JSON.parse(JSON.stringify(this.slotData.rooms));
        } else {
          this.rooms = [];
        }

        if (this.slotData.patients) {
          this.patients = JSON.parse(JSON.stringify(this.slotData.patients));

          for (let i = 0; i < this.patients.length; i++) {
            this.patients[i].priceBrut = +(this.patients[i].price * 1.19).toFixed(2)
          }
        } else {
          this.patients = [];
        }

        this.product = this.slotData.product

        this.isTemporary = this.slotData.is_temporary;
        this.travelTimePrev = this.slotData.travel_time_prev || 0;
        this.travelTimeAfter = this.slotData.travel_time_after || 1;

        this.location = this.slotData.location

        if (this.location === undefined && this.locations) {
          for (let i = 0; i < this.locations.length; i++) {
            if (this.locations[i].value == "internal") {
              this.location = this.locations[i].key
            }
          }
        }

        this.acceptedByPatient = this.slotData.accepted_by_patient
        this.acceptedByTherpist = this.slotData.accepted_by_therapist

        this.customAddress = this.slotData.custom_address

        this.suggestForPatient = false

        this.public = this.slotData.public
        this.numPatients = this.slotData.num_patients

        this.status = undefined
        for (let i = 0; i < this.statusChoices.length; i++) {
          if (this.statusChoices[i].key === this.slotData.status) {
            this.status = this.statusChoices[i]
          }
        }
      }
    },
    getReadableDate(date) {
      if (date) return this.$moment(date).format("DD.MM.YYYY");
      else return undefined;
    },
    getReadableTime(time) {
      if (time) return time.substring(0, 5)
      else return "";
    },
    async confirm(sendChangedMail) {
      const { valid } = await this.$refs.slotEditForm.validate();

      if (valid) {
        this.$emit("loading", true);

        let teachers = [];
        for (let i = 0; i < this.teachers.length; i++) {
          teachers.push(this.teachers[i].id);
        }

        let rooms = [];
        for (let i = 0; i < this.rooms.length; i++) {
          rooms.push(this.rooms[i].id);
        }

        let patients = []
        for (let i = 0; i < this.patients.length; i++) {
          if (this.patients[i].is_cancelled) continue;
          patients.push({
            user: this.patients[i].user.id,
            id: this.patients[i].id,
            price: this.patients[i].price
          })
        }


        let formData = {
          start_time: this.startTime,
          end_time: this.endTime,
          description: this.description,
          date: this.$moment(this.date).format("YYYY-MM-DD"),
          is_temporary: false,
          teachers: teachers,
          rooms: rooms,
          location: this.location,
          product: this.product.id,
          patients: JSON.stringify(patients),
          is_temporary: this.isTemporary,
          color_code: this.rgbStringToHex(this.colorCode),
          accepted_by_therapist: this.acceptedByTherpist,
          custom_address: this.customAddress,
          status: this.status?.key,
          public: this.public,
          num_patients: this.numPatients,
          travel_time_prev: this.travelTimePrev,
          travel_time_after: this.travelTimeAfter,
          prev: this.prev?.id,
          send_appointment_changed: sendChangedMail
        };

        if (this.slotData && !this.slotData.id) {
          formData.accepted_by_patient = !this.suggestForPatient
        }

        if (this.slotData && this.slotData.id) {
          formData["id"] = this.slotData.id;
          this.patchOrPostData(formData);
        } else {
          this.patchOrPostData(formData);
        }
      }
    },
    rgbStringToHex(rgbString) {
      if (!rgbString) {
        return null;
      }
      const match = rgbString.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
      if (!match) return null;

      const [, r, g, b] = match.map(Number);
      return (
        "#" +
        [r, g, b]
          .map((x) => {
            const hex = x.toString(16);
            return hex.length === 1 ? "0" + hex : hex;
          })
          .join("")
      );
    },
    hexToRgbString(hex) {
      // Remove leading # if present
      hex = hex.replace(/^#/, "");

      // Handle shorthand hex (#abc)
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((c) => c + c)
          .join("");
      }

      if (hex.length !== 6) return null;

      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);

      return `rgb(${r}, ${g}, ${b})`;
    },
    patchOrPostData(formData) {
      this.axios
        .patch(this.axios.defaults.baseURL + "calendar/timeslots/patch/", formData)
        .then((resp) => {
          this.$emit("close", resp.data);
        })
        .catch((e) => {
          this.$emit("loading", false);
          console.log(e);
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
        });
    },
    switchDay(value) {
      this.dateIndex += value;

      if (this.dateIndex < 1) {
        this.dateIndex = 1;
      } else if (this.dateIndex > this.duration) {
        this.dateIndex = this.duration;
      }
    },
    fetchPatients(query) {
      console.log("??")
      let searchString = "?active=1"
      if (query) {
        searchString += "&query=" + query
      }

      this.axios.get(this.axios.defaults.baseURL + "user/users/" + searchString)
        .then((resp) => {
          this.availPatients = resp.data.results
        })
        .catch((e) => {
          console.log(e)
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
        })
    },
    fetchTimeSlotData() {
      return new Promise((resolve, reject) => {
        if (!this.$checkFunctionPermission(["own_calendar_detail"])) {
          resolve()
          return
        }

        this.axios
          .post(
            this.axios.defaults.baseURL + "calendar/timeslots/ressources/",
            {
              date: this.slotData.date,
              start_time: this.slotData.time_units[0].start_time,
              end_time: this.slotData.time_units[this.slotData.time_units.length - 1]
                .end_time,
              time_slot: this.slotData.id ? this.slotData.id : null,
            }
          )
          .then((resp) => {
            this.teachersFiltered = resp.data.avail_teachers;
            this.roomsFiltered = resp.data.avail_rooms;

            this.availTeachers = resp.data.teachers_all;
            this.availRooms = resp.data.rooms_all;


            if (!this.slotData.id) {
              for (let i = 0; i < this.availTeachers.length; i++) {
                if (this.availTeachers[i].id == this.$store.state.id) {
                  let found = false
                  for (let j = 0; j < this.teachers.length; j++) {
                    if (this.teachers[j].id == this.$store.state.id) {
                      found = true
                      break
                    }
                  }

                  if (!found) {
                    this.teachers.push(this.availTeachers[i])
                  }
                }
              }
            }

            resolve()
          })
          .catch((e) => {
            console.log(e)
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
            reject()
          })
      })
    },
    fetchPrevs(searchQ) {
      return new Promise((resolve, reject) => {
        let query = "?prev=1&size=10000&date=" + this.slotData.date

        if (searchQ && searchQ.length > 2) {
          query += "&query=" + searchQ
        }
        if (searchQ && searchQ.length == 1) {
          return
        }

        this.axios
          .get(
            this.axios.defaults.baseURL + "calendar/timeslots/" + query
          )
          .then((resp) => {
            this.availPrevs = resp.data.results

            resolve()
          })
          .catch((e) => {
            console.log(e)
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
            reject()
          })
      })
    },
    fetchProducts() {
      return new Promise((resolve, reject) => {
        this.axios
          .get(
            this.axios.defaults.baseURL + "media/products/?size=10000"
          )
          .then((resp) => {
            this.availProducts = resp.data.results

            if (this.product == undefined) {
              for (let i = 0; i < this.availProducts.length; i++) {
                if (this.availProducts[i].is_default) {
                  this.product = this.availProducts[i]
                }
              }
            }
            else {
              for (let i = 0; i < this.availProducts.length; i++) {
                if (this.availProducts[i].id == this.product.id) {
                  this.product = this.availProducts[i]
                }
              }
            }

            if (this.product == undefined && this.availProducts.length == 1) {
              this.product = this.availProducts[0]
            }

            resolve()
          })
          .catch((e) => {
            console.log(e)
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
            reject()
          })
      })
    },
  },

  mounted() {

  },
};
</script>
